import { ChangeDetectorRef, Component, Input, Output, EventEmitter, OnInit, OnChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { MITableComponent } from 'app/modules/shared/components/mi-table/mi-table.component';
import { CommonBindingDataService } from 'app/modules/shared/services/common-binding-data.service';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { UserManagementService } from 'app/modules/user-management/services/user-management.service';
import { AppSettings } from 'app/modules/shared/app.settings';
import { AppIcons } from 'app/modules/shared/app.icons';
import { Country } from 'app/modules/shared/models/country';
import { Language } from 'app/modules/shared/models/language';
import { EntityList } from 'app/modules/shared/models/entity.models';
import { Pagination } from 'app/modules/shared/models/listResponse.models';
import { Column } from 'app/modules/shared/models/attribute.models';

interface ExtendedColumn extends Column {
  attributeCode?: string;
  attributeIndex?: number;
  attributeId?: number;
  presetValues?: Array<{ labelKey: string; labelValue: string }>;
  isSortable?: boolean;
  inputCode?: string;
}

interface UserTableRow {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  userRole: string;
  username: string;
  active: Record<string, string>;
  fullName: string;
  [key: string]: unknown;
}

interface UserSearchData extends EntityList {
  userType: string[];
  typeId: string[];
  roleName?: string;
}

@Component({
  selector: 'app-site-users',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AccessProviderDirective,
    MITableComponent
  ],
  templateUrl: './site-users.component.html',
  styleUrl: './site-users.component.scss'
})
export class SiteUsersComponent implements OnInit, OnChanges {
  @ViewChild(MITableComponent) tableRef: MITableComponent;
  @Input() isActive: boolean = false;
  @Input() isSiteReady: boolean = false;
  @Input() siteEntityId: string;
  @Output() countUpdate = new EventEmitter<number>();
  @Output() saveSuccess = new EventEmitter<void>();

  miIcons = AppIcons;
  isLoading = true;
  allColumns: ExtendedColumn[] = [];
  pagination: Pagination;
  totalRecords: number = 0;
  tableData: UserTableRow[] = [];
  country: Country;
  language: Language;
  entityData: UserSearchData = {
    actionStatus: "",
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    defaultSortColumn: "updatedAt",
    defaultSortType: "desc",
    status: 'Active',
    userType: [AppSettings.ENTITY_CODE.SITE],
    typeId: []
  };
  uniqueId: string = 'site_user_grid';
  isEntity: boolean = false;

  constructor(
    private cs: CommonBindingDataService,
    private configService: ConfigService,
    private cd: ChangeDetectorRef,
    private userManagementService: UserManagementService,
    private router: Router
  ) { }

  ngOnInit() {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.initializeColumn();
    this.setEntityDataTypeId();
  }

  ngOnChanges() {
    if (this.isActive && this.isSiteReady && this.siteEntityId) {
      this.setEntityDataTypeId();
      this.getData();
    }
  }

  setEntityDataTypeId() {
    if (!this.entityData.typeId.length) {
      if (this.siteEntityId) {
        this.entityData.typeId = [this.siteEntityId];
        this.entityData.forTenantCode = this.configService.getForTenantCode();
        this.entityData.countryCode = AppSettings.HEADER_COUNTRY_CODE;
      }
    }
  }

  initializeColumn() {
    this.allColumns = [
      {
        attributeCode: "firstName",
        attributeIndex: 0,
        field: "firstName",
        header: this.cs.getLabel('site.lbl_first_name'),
        isSortable: true
      },
      {
        attributeCode: "lastName",
        attributeIndex: 1,
        field: "lastName",
        header: this.cs.getLabel('site.lbl_last_name'),
        isSortable: true
      },
      {
        attributeCode: "email",
        attributeIndex: 2,
        field: "email",
        header: this.cs.getLabel('site.lbl_email'),
        isSortable: true
      },
      {
        "attributeCode": "contactNumber",
        "attributeIndex": 4,
        "isSortable": true,
        "field": "contactNumber",
        "header": this.cs.getLabel('manage_users.columns.lbl_phone_number')
      },
      {
        "attributeCode": "user_type",
        "attributeIndex": 7,
        "isSortable": false,
        "field": "userType",
        "header": this.cs.getLabel('manage_users.columns.lbl_user_type')
      },
      {
        attributeCode: "user_status",
        attributeIndex: 4,
        field: "active",
        header: this.cs.getLabel('site.lbl_status'),
        isSortable: true
      }
    ];
  }

  getData() {
    this.isLoading = true;
    this.searchEntity();
  }

  searchEntity() {
    this.userManagementService.searchEntityUser(this.entityData).subscribe((res: any) => {
      this.pagination = res?.pagination;
      this.totalRecords = res?.count;
      this.tableData = res.data?.map((user: any) => ({
        ...user,
        fullName: `${user.firstName} ${user.lastName}`,
        active: user.active ? { Active: 'Active' } : { Inactive: 'Inactive' }
      })) || [];
      this.countUpdate.emit(this.totalRecords);
      this.isLoading = false;
    });
  }

  onPageChange(event: any) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  onSearchValueChanges(event: string) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }


  itemPerPageChange(event: number) {
    this.entityData.limit = event;
    this.searchEntity();
  }

  onSortData(event: any) {
    this.entityData.defaultSortColumn = event.attributeIndex;
    this.entityData.defaultSortType = (event.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }

  onRowClick(user: any) {
    this.router.navigate(['app/manage-users/user-details'], { queryParams: { id: user.data?.userId } });
  }

}